/**
 * Poll API
 *
 * Fetches async task results via polling.
 * Used when chat stream returns an asyncTask (e.g., flight search).
 */
import type { PollHttpMethod, PollResponse } from '@contracts';
import { buildHeaders, buildQueryParams, buildUrl } from '@/api/utils';
import { FEATURE_FLAGS } from '@/features/flights/constants';
import {
  MOCK_POLL_PROCESSING,
  MOCK_POLL_COMPLETED,
} from '@/api/contracts/templates/flights/api-response/chat-stream-async-poll';

const MOCK_PROCESSING_CALLS_BEFORE_COMPLETE = 3;
let mockCallCount = 0;

function getMockPollResponse(): PollResponse {
  mockCallCount += 1;
  if (mockCallCount <= MOCK_PROCESSING_CALLS_BEFORE_COMPLETE) {
    return MOCK_POLL_PROCESSING as PollResponse;
  }
  mockCallCount = 0;
  return MOCK_POLL_COMPLETED as PollResponse;
}

interface CbErrorDetails {
  errorMessage?: string;
  errorDescription?: string;
}

/**
 * Thrown when the poll API returns a non-2xx response with CB error body.
 * Carries errorMessage and errorDescription directly from CB response.
 */
export class PollApiError extends Error {
  readonly errorMessage: string;
  readonly errorDescription: string;

  constructor(details: CbErrorDetails) {
    super(details.errorDescription ?? details.errorMessage ?? 'Poll API error');
    this.name = 'PollApiError';
    this.errorMessage = details.errorMessage ?? '';
    this.errorDescription = details.errorDescription ?? '';
  }
}

function parseCbErrorDetails(body: unknown): CbErrorDetails | null {
  if (typeof body === 'object' && body !== null && 'errorDetails' in body) {
    const { errorDetails } = body as { errorDetails?: CbErrorDetails };
    if (errorDetails?.errorMessage || errorDetails?.errorDescription) {
      return errorDetails;
    }
  }
  return null;
}

/**
 * Fetch poll result for an async task.
 * When USE_MOCK_ASYNC_POLL is enabled, returns mock data instead of hitting the API.
 * Throws PollApiError with parsed error details on non-2xx responses.
 */
export async function fetchPollResult(
  pollEndpoint: string,
  params: Record<string, string>,
  method: PollHttpMethod,
  signal?: AbortSignal,
): Promise<PollResponse> {
  if (FEATURE_FLAGS.USE_MOCK_ASYNC_POLL) {
    return getMockPollResponse();
  }

  const isGet = method === 'GET';

  const url = isGet ? buildUrl(pollEndpoint, buildQueryParams(params)) : buildUrl(pollEndpoint);

  const response = await fetch(url, {
    method,
    headers: buildHeaders(),
    ...(signal && { signal }),
    ...(!isGet && { body: JSON.stringify(params) }),
  });

  if (!response.ok) {
    try {
      const errorBody: unknown = await response.json();
      const details = parseCbErrorDetails(errorBody);
      if (details) {
        throw new PollApiError(details);
      }
    } catch (error: unknown) {
      if (error instanceof PollApiError) throw error;
    }
    throw new Error(`Poll request failed: ${response.status}`);
  }

  return response.json() as Promise<PollResponse>;
}
